// Step 4: languages and wording. English is always on; the company can add any language
// they want. Each language needs every word staff will see: the five alert instructions,
// the alert titles, the lock screen and red card, and the app's own screens. The English
// sits next to each one. A language can't be sent for approval until every word is there,
// so staff never see a mix. English and Arabic start from our standard words.

import { useMemo, useRef, useState } from 'react';
import { MAX_PHRASE } from '../../../../../spec/src/formats.ts';
import { PHRASE_GROUPS, STANDARD_PHRASES, blanksIn, type PhraseKey } from '../../../../../spec/src/phrases.ts';
import { languageProgress, STANDARD_WORDING, type SiteDraft } from '../../../../src/draft.ts';
import { message } from '../../api.ts';
import { LEVELS } from '../../format.ts';
import { ALL_LANGUAGES, byUsualOrder, englishName, ownName } from '../../languages.ts';
import type { SiteView } from '../../types.ts';
import { saveDraft } from './draftSave.ts';
import { useSaveWhenTypingStops } from '../../ui.tsx';

const ORDER = ['alert', 'shelter', 'evacuate', 'clear', 'false_alarm'] as const;
type Level = (typeof ORDER)[number];
type Words = Record<Level, string>;
type Phrases = Partial<Record<string, string>>;
const blankWords = (): Words => ({ alert: '', shelter: '', evacuate: '', clear: '', false_alarm: '' });

/** Languages written in Arabic letters get the Arabic font. */
const ARABIC_SCRIPT = new Set(['ar', 'fa', 'ur', 'ps', 'sd', 'ug', 'ckb', 'ks']);

const BLANK_MEANING: Record<string, string> = {
  site: "the site's name",
  zone: 'the zone number',
  zones: 'the zone or zones',
  message: 'the alert instruction',
  then: 'what to do next',
  title: "the alert's title",
  count: 'a number',
  accepted: 'how many phones got it',
  attempted: 'how many phones it was sent to',
  failed: 'how many are being retried',
  name: 'a name',
  time: 'a date and time',
  language: 'a language',
  languages: "the site's languages",
  link: 'the link',
  summary: 'what the change does',
};

export function WordingStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  const [messages, setMessages] = useState<Record<string, Words>>(() => structuredClone(draft.messages) as Record<string, Words>);
  const [phrases, setPhrases] = useState<Record<string, Phrases>>(() => {
    const p = structuredClone(draft.phrases ?? {}) as Record<string, Phrases>;
    // Arabic starts from our words (and picks up any we've added since), so there's something to check and change.
    if (draft.messages.ar) p.ar = { ...STANDARD_PHRASES.ar, ...p.ar };
    return p;
  });
  const [checked, setChecked] = useState<string[]>(draft.checked);
  const [current, setCurrent] = useState('en');
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const langs = Object.keys(messages).sort(byUsualOrder);
  const progress = (l: string) => languageProgress({ messages, phrases }, l);

  // Saves go one after another, so a quick edit-then-tick can't arrive out of order.
  const queue = useRef(Promise.resolve());
  const persist = (m: Record<string, Words>, p: Record<string, Phrases>, c: string[]) => {
    setStatus('Saving…');
    setError(null);
    const toSave = Object.fromEntries(Object.entries(p).filter(([l]) => l !== 'en' && l in m));
    return (queue.current = queue.current.then(async () => {
      try {
        await saveDraft(site, { messages: m as SiteDraft['messages'], phrases: toSave, checked: c.filter((l) => l in m) });
        setStatus(site.live ? "Saved · phones get it once it's approved on the ops tablet" : 'Saved');
        reload();
      } catch (e) {
        setStatus(null);
        setError(message(e));
      }
    }));
  };
  // Typing saves itself once it pauses, and straight away when the box loses focus.
  const autosave = useSaveWhenTypingStops(() => persist(messages, phrases, checked));
  const save = autosave.flush;

  const uncheck = (l: string) => checked.includes(l) && setChecked(checked.filter((x) => x !== l));
  const editMessage = (l: string, level: Level, text: string) => {
    setMessages({ ...messages, [l]: { ...messages[l], [level]: text } });
    uncheck(l);
    setStatus(null);
    autosave.edited();
  };
  const editPhrase = (l: string, key: PhraseKey, text: string) => {
    setPhrases({ ...phrases, [l]: { ...phrases[l], [key]: text } });
    uncheck(l);
    setStatus(null);
    autosave.edited();
  };
  const tick = (l: string, on: boolean) => {
    const next = on ? [...checked, l] : checked.filter((x) => x !== l);
    setChecked(next);
    persist(messages, phrases, next);
  };
  const add = (code: string) => {
    const m = { ...messages, [code]: code === 'ar' ? { ...(STANDARD_WORDING.ar as Words) } : blankWords() };
    const p = { ...phrases, [code]: code === 'ar' ? { ...STANDARD_PHRASES.ar } : {} };
    setMessages(m);
    setPhrases(p);
    setCurrent(code);
    setAdding(false);
    setSearch('');
    persist(m, p, checked);
  };
  const remove = (code: string) => {
    if (!confirm(`Take ${englishName(code)} off? Staff who chose it will get English.`)) return;
    const m = { ...messages };
    const p = { ...phrases };
    delete m[code];
    delete p[code];
    const c = checked.filter((x) => x !== code);
    setMessages(m);
    setPhrases(p);
    setChecked(c);
    setCurrent('en');
    persist(m, p, c);
  };

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_LANGUAGES.filter((l) => !(l.code in messages) && (!q || l.english.toLowerCase().includes(q) || l.own.toLowerCase().includes(q)));
  }, [search, messages]);

  const lang = langs.includes(current) ? current : 'en';
  const p = progress(lang);
  const arabicFont = ARABIC_SCRIPT.has(lang);
  const input = (value: string, onChange: (v: string) => void, label: string, max: number, rows = 2) => (
    <textarea
      className={arabicFont ? 'input arabic' : 'input'}
      dir="auto"
      rows={rows}
      maxLength={max}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={save}
    />
  );

  return (
    <>
      <p className="muted" style={{ maxWidth: 820 }}>
        Every word anyone at this site sees, in each language it offers: staff phones and the ops tablet. English is always on. Add any language the company wants and
        fill in every word (the English is next to each one). It can be sent for approval once it's complete; then everyone can choose it.
      </p>

      <div className="stack" style={{ gap: 10 }}>
        <span className="card-label">Languages staff can choose</span>
        <div className="row wrap" role="group" aria-label="Languages">
          {langs.map((l) => {
            const pr = progress(l);
            return (
              <button key={l} className={l === lang ? 'pill on' : 'pill'} aria-pressed={l === lang} onClick={() => setCurrent(l)}>
                {englishName(l)}
                <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.8 }}>{pr.complete ? '✓' : `${pr.total - pr.done} to go`}</span>
              </button>
            );
          })}
          <button className="pill" onClick={() => setAdding(!adding)} aria-expanded={adding}>
            + Add a language
          </button>
        </div>
        {adding && (
          <div className="card" style={{ gap: 10, maxWidth: 520 }}>
            <input
              className="input"
              placeholder="Search: Portuguese, Swahili, Français…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              aria-label="Search languages"
            />
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <div className="stack" style={{ gap: 4 }}>
                {matches.map((l) => (
                  <button key={l.code} className="linkish" style={{ textAlign: 'left', padding: '6px 2px' }} onClick={() => add(l.code)}>
                    {l.english}
                    {l.own !== l.english && <span className="muted"> · {l.own}</span>}
                  </button>
                ))}
                {matches.length === 0 && <span className="faint small">No language by that name.</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row wrap" style={{ justifyContent: 'space-between', maxWidth: 1000 }}>
        <span className="stack" style={{ gap: 2 }}>
          <strong>
            {englishName(lang)}
            {ownName(lang) !== englishName(lang) && <span className="muted"> · {ownName(lang)}</span>}
          </strong>
          <span className={p.complete ? 'green small' : 'amber small'}>
            {p.complete ? `Complete: all ${p.total} words` : `${p.done} of ${p.total} words filled in. It can be sent for approval once they all are.`}
          </span>
        </span>
        <span className="row" style={{ gap: 14 }}>
          <label className="row small" style={{ gap: 6, color: checked.includes(lang) ? 'var(--green)' : 'var(--amber)' }}>
            <input type="checkbox" checked={checked.includes(lang)} disabled={!p.complete} onChange={(e) => tick(lang, e.target.checked)} />
            {checked.includes(lang) ? `Checked by someone fluent in ${englishName(lang)}` : 'Not yet checked'}
          </label>
          {lang !== 'en' && (
            <button className="linkish small" onClick={() => remove(lang)}>
              Take {englishName(lang)} off
            </button>
          )}
        </span>
      </div>
      <span className="faint small">{status}</span>
      {error && (
        <div className="note red" role="alert">
          {error}
        </div>
      )}

      <Section title="Alert instructions" note="What staff should do, for each level.">
        {ORDER.map((level) => (
          <Row
            key={level}
            left={
              <span className="stack" style={{ gap: 4 }}>
                <span className="row" style={{ gap: 8, fontWeight: 500 }}>
                  <span className="dot" style={{ background: LEVELS[level].colour }} />
                  {LEVELS[level].name}
                </span>
                {lang !== 'en' && <span className="muted small">{messages.en[level]}</span>}
              </span>
            }
          >
            {input(messages[lang]?.[level] ?? '', (v) => editMessage(lang, level, v), `${LEVELS[level].name} in ${englishName(lang)}`, 200)}
          </Row>
        ))}
      </Section>

      {lang === 'en' ? (
        <p className="faint small">English app words are built in. Add a language to see every word to fill in.</p>
      ) : (
        PHRASE_GROUPS.map((g) => (
          <Section key={g.name} title={g.name}>
            {g.keys.map((key) => {
              const english = STANDARD_PHRASES.en[key];
              const value = phrases[lang]?.[key] ?? '';
              const needs = blanksIn(english);
              const lost = value.trim() !== '' && blanksIn(value).join() !== needs.join();
              return (
                <Row key={key} left={key === 'change.separator' ? <span>Between the things a change does, e.g. “; ”</span> : <English text={english} />}>
                  <span className="stack" style={{ gap: 4 }}>
                    {input(value, (v) => editPhrase(lang, key, v), `${english} in ${englishName(lang)}`, MAX_PHRASE, english.includes('\n') ? 8 : 2)}
                    {lost && (
                      <span className="red small">
                        Keep {needs.map((b) => `{${b}}`).join(' and ')} as written: the app puts {needs.map((b) => BLANK_MEANING[b]).join(' and ')} there.
                      </span>
                    )}
                  </span>
                </Row>
              );
            })}
          </Section>
        ))
      )}
    </>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 8, maxWidth: 1000 }}>
      <span className="card-label">
        {title}
        {note && <span className="faint" style={{ textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>{note}</span>}
      </span>
      <div className="table">{children}</div>
    </div>
  );
}

function Row({ left, children }: { left: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="tr" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)', alignItems: 'start', gap: 16 }}>
      <span style={{ paddingTop: 8 }}>{left}</span>
      {children}
    </div>
  );
}

/** The English, with its blanks shown as blanks. */
function English({ text }: { text: string }) {
  const parts = text.split(/(\{(?:site|zone|zones|message|then|title|count|accepted|attempted|failed|name|time|language|languages|link|summary)\})/);
  return (
    <span style={{ whiteSpace: 'pre-line' }}>
      {parts.map((part, i) =>
        /^\{\w+\}$/.test(part) ? (
          <span key={i} className="mono small" style={{ padding: '1px 6px', borderRadius: 6, background: 'var(--line2)', color: 'var(--text)' }} title={BLANK_MEANING[part.slice(1, -1)]}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
}
